/* AOV grower — keeps the PDP offer panel and the rail steppers in step with
 * the real cart. It does NOT touch the Add to bag button: that reads
 * "Add to bag · {price}" always (decided 2026-10-19). snippets/pdp-aov-panel.liquid renders the
 * first state server-side; this file keeps it current.
 *
 * COUNT EVERY QUALIFYING UNIT, however it got there: this PDP, the rail,
 * another PDP, the cart drawer. The custom drawer announces nothing when a
 * quantity changes (mini-cart.js swaps .drawer__inner silently), and rb-sale.js
 * — which does watch it — only loads while the BFCM switches are on. So this
 * watches the same three sources itself: Dawn's pubsub cart-update,
 * theme:product:add, and a MutationObserver on the drawer, plus rb:sale:cart
 * when it exists. One debounced /cart.js per burst. It never WRITES into the
 * drawer in response to those signals (that loops /cart.js through rb-sale's
 * observer — see memory "cart drawer integration traps").
 *
 * OPTIMISTIC (2026-10-20): a tap changes the card at once and reconciles when
 * the request returns; see "optimistic steppers" below. Measured before: 2.1s
 * from tap to stepper, over three serial requests.
 *
 * STEPPERS DO NOT OPEN THE DRAWER (brief: only the main Add to bag opens it).
 * fetchCart() always opens it, so after a stepper change the drawer's contents
 * and the header count are refreshed here directly, without opening. On the
 * sale theme adds still go through RBSale.add (BFCM rule 7) with open:false.
 *
 * "UNLOCKED" READS THE CART, not the config: claimed only when the qualifying
 * lines carry a discount allocation. Pixoo's discount is line-level ($10 split
 * $5 per line, measured 2026-10-18).
 */
(function () {
  var root = document.querySelector('[data-aov]');
  if (!root || root.hasAttribute('data-aov-ready')) return;
  root.setAttribute('data-aov-ready', '');

  var cfgEl = root.querySelector('[data-aov-config]');
  if (!cfgEl) return;
  var cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var ids = {};
  (cfg.productIds || []).forEach(function (id) { if (id) ids[String(id)] = 1; });
  var need = Number(cfg.need) || 2;
  var line = root.querySelector('[data-aov-line]');
  var pill = root.querySelector('[data-aov-pill]');
  var cards = Array.prototype.slice.call(root.querySelectorAll('[data-aov-card]'));
  var shopRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function word(n) { return (cfg.words && cfg.words[n]) || String(n); }
  function fill(tpl, left) {
    return String(tpl || '')
      .split('{qty_word}').join(word(need)).split('{qty}').join(String(need))
      .split('{left_word}').join(word(left)).split('{left}').join(String(left));
  }

  /* ---- state -----------------------------------------------------------
     lastCart is the newest cart we trust. A card with a pending `target` shows
     that number instead of the cart's until the request settles (optimistic
     UI, 2026-10-20: 2.1s of an unchanged button read as a failed tap).

     NEWEST WINS. Every cart carries a sequence number: a read is numbered when
     it STARTS, a write's result when it COMPLETES, so a read that began before
     our write landed can never overwrite the write's result. */
  var lastCart = null, lastSeq = 0, seqN = 0, lastAcceptAt = 0;
  var cardState = new Map();
  cards.forEach(function (c) { cardState.set(c, { target: null, sent: null, inflight: false }); });
  function nextSeq() { return ++seqN; }
  function qtyIn(cart, pid) {
    var q = 0;
    (cart && cart.items || []).forEach(function (li) { if (String(li.product_id) === String(pid)) q += li.quantity; });
    return q;
  }
  function confirmedQty(card) {
    var pid = card.getAttribute('data-product-id');
    if (lastCart) return qtyIn(lastCart, pid);
    var qty = card.querySelector('[data-aov-qty]');
    return qty ? Number(qty.textContent) || 0 : 0;
  }
  function anyPending() {
    var p = false;
    cardState.forEach(function (st) { if (st.target !== null || st.inflight) p = true; });
    return p;
  }

  /* ---- render ----------------------------------------------------------
     "UNLOCKED" AND THE DUO LINE ARE CART-ONLY. Both claim something about the
     bag (Pixoo has discounted; the duo is in it), so neither is ever shown on
     a pending tap. The count and the steppers are optimistic. */
  function paintCard(card, q) {
    var add = card.querySelector('[data-aov-add]');
    var step = card.querySelector('[data-aov-step]');
    var qty = card.querySelector('[data-aov-qty]');
    if (add && add.hidden !== (q > 0)) add.hidden = q > 0;
    if (step && step.hidden !== (q === 0)) step.hidden = q === 0;
    if (qty && qty.textContent !== String(q)) qty.textContent = String(q);
    card.classList.toggle('is-in-bag', q > 0);
  }
  function paint() {
    if (!lastCart) {
      cardState.forEach(function (st, card) { if (st.target !== null) paintCard(card, st.target); });
      return;
    }
    var cart = lastCart;
    var have = 0, applied = 0, perProduct = {};
    (cart.items || []).forEach(function (li) {
      var pid = String(li.product_id);
      perProduct[pid] = (perProduct[pid] || 0) + li.quantity;
      if (!ids[pid]) return;
      have += li.quantity;
      (li.line_level_discount_allocations || []).forEach(function (d) { applied += Number(d.amount) || 0; });
    });

    var unlocked = have >= need && applied > 0;
    var duoQ = cfg.duoProductId ? (perProduct[String(cfg.duoProductId)] || 0) : 0;
    var duoMode = !unlocked && duoQ > 0;

    /* Optimistic count: the cart's units with each pending card's target
       swapped in for its confirmed quantity. */
    var haveOpt = have, pending = false;
    cardState.forEach(function (st, card) {
      if (st.target === null) return;
      pending = true;
      var pid = card.getAttribute('data-product-id');
      if (ids[pid]) haveOpt += st.target - (perProduct[pid] || 0);
    });

    var text = null;
    if (unlocked) text = fill(cfg.lines.unlocked, 0);
    else if (duoMode) text = cfg.lines.duo || '';
    else if (haveOpt >= need) text = pending ? null : ''; /* reached on a guess: hold the line until the cart says */
    else if (haveOpt === 0) text = fill(cfg.lines.zero, need);
    else text = fill(cfg.lines.progress, need - haveOpt);
    if (line && text !== null && line.textContent !== text) line.textContent = text;
    if (pill) {
      if (pill.hidden !== duoMode) pill.hidden = duoMode;
      var pt = unlocked ? 'Unlocked ✦' : haveOpt + ' of ' + need + ' in bag';
      if (pill.textContent !== pt) pill.textContent = pt;
      pill.classList.toggle('aov-panel__pill--on', unlocked);
    }

    cardState.forEach(function (st, card) {
      paintCard(card, st.target !== null ? st.target : (perProduct[card.getAttribute('data-product-id')] || 0));
    });
  }
  function accept(cart, seq) {
    if (!cart || !cart.items || seq <= lastSeq) return false;
    lastCart = cart; lastSeq = seq; lastAcceptAt = Date.now();
    document.querySelectorAll('cart-counter').forEach(function (el) {
      var count = cart.item_count;
      var protectionId = Number(el.getAttribute('data-order-protection-id'));
      (cart.items || []).forEach(function (li) { if (li.product_id === protectionId) count -= li.quantity; });
      if (typeof el.updateCount === 'function') el.updateCount(count);
    });
    return true;
  }

  /* ---- cart watching: one debounced /cart.js ---------------------------- */
  var timer = null, inflight = false, again = false;
  function fetchCartJson() { return fetch(shopRoot + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }
  function refresh() {
    if (inflight) { again = true; return; }
    inflight = true;
    var seq = nextSeq();
    fetchCartJson().then(function (c) { if (accept(c, seq)) paint(); }).catch(function () {}).then(function () {
      inflight = false;
      if (again) { again = false; refresh(); }
    });
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(refresh, 250); }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }
  document.addEventListener('theme:product:add', schedule);
  /* rb-sale.js has already read the cart when it fires this: use its copy, no
     second read. Ignored while a tap is settling or just after one — that is
     our own drawer refresh echoing back, and its cart may predate our write. */
  document.addEventListener('rb:sale:cart', function () {
    if (anyPending() || Date.now() - lastAcceptAt < 1500) return;
    if (window.RBSale && RBSale.cart && RBSale.cart.items) { if (accept(RBSale.cart, nextSeq())) paint(); }
    else schedule();
  });
  var drawerInner = document.querySelector('#cart-drawer .drawer__inner');
  var drawerMO = null;
  /* Our own drawer write is dropped with takeRecords below, but other scripts
     redecorate the drawer straight after it, which re-fires this. Same rule as
     rb:sale:cart: not while a tap is settling or just after one. */
  if (drawerInner) {
    drawerMO = new MutationObserver(function () { if (!anyPending() && Date.now() - lastAcceptAt >= 1500) schedule(); });
    drawerMO.observe(drawerInner, { childList: true, subtree: true });
  }

  /* ---- silent drawer refresh (no opening) ------------------------------
     Section HTML only — the cart itself is already in hand. Our own write is
     dropped from the observer (takeRecords) so it does not trigger a re-read. */
  var drawerTimer = null;
  function refreshDrawerSilently() {
    clearTimeout(drawerTimer);
    drawerTimer = setTimeout(function () {
      var holder = document.querySelector('.shopify-section-group-header-group[id*="cart-drawer"]');
      var inner = document.querySelector('#cart-drawer .drawer__inner');
      if (!holder || !inner) return;
      var sid = holder.id.replace('shopify-section-', '');
      fetch(shopRoot + 'cart?section_id=' + encodeURIComponent(sid))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
          if (!fresh) return;
          inner.innerHTML = fresh.innerHTML;
          if (drawerMO) drawerMO.takeRecords();
        }).catch(function () {});
    }, 300);
  }

  /* ---- writes: each resolves to the cart AFTER the change --------------- */
  function addUnits(variantId, n, pid, expect) {
    var item = { id: Number(variantId), quantity: n };
    var p = (window.RBSale && typeof RBSale.add === 'function')
      ? RBSale.add(item, { open: false })
      : fetch(shopRoot + 'cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] })
        }).then(function (r) { if (!r.ok) throw new Error('add ' + r.status); return null; });
    /* RBSale.add resolves to its refreshed cart — but to a STALE one if a read
       was already in flight. Trust it only if it shows the add. */
    return p.then(function (c) { return (c && c.items && qtyIn(c, pid) === expect) ? c : fetchCartJson(); });
  }
  function changeUnits(variantId, n) {
    var find = function (cart) { return (cart && cart.items || []).filter(function (x) { return String(x.variant_id) === String(variantId); })[0]; };
    var li = find(lastCart);
    return (li ? Promise.resolve(li) : fetchCartJson().then(find)).then(function (line) {
      if (!line) throw new Error('no line');
      return fetch(shopRoot + 'cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity + n) })
      }).then(function (r) { if (!r.ok) throw new Error('change ' + r.status); return r.json(); });
    });
  }

  /* ---- optimistic steppers ---------------------------------------------
     A tap moves the card's target and paints at once. One request per card at
     a time; taps made meanwhile accumulate and go in the next request.
     FAILURE OR A CAP SETTLES ONCE: the card drops straight to what the cart
     holds — never through intermediate numbers — queued taps are discarded,
     and "Couldn't update your bag" shows until the next tap. A cap is a
     success whose cart holds less than was sent. */
  function showError(card) {
    if (card.querySelector('.aov-ctl__error')) return;
    var p = document.createElement('p');
    p.className = 'aov-ctl__error'; p.setAttribute('role', 'status');
    p.textContent = 'Couldn’t update your bag';
    card.querySelector('[data-aov-ctl]').appendChild(p);
  }
  function clearError(card) { var e = card.querySelector('.aov-ctl__error'); if (e) e.remove(); }
  function settle(card, st) { st.target = null; st.sent = null; showError(card); paint(); }
  function flush(card) {
    var st = cardState.get(card);
    if (st.inflight || st.target === null) return;
    var pid = card.getAttribute('data-product-id');
    var from = confirmedQty(card);
    var delta = st.target - from;
    if (delta === 0) { st.target = null; paint(); return; }
    st.inflight = true; st.sent = st.target;
    var req = delta > 0 ? addUnits(card.getAttribute('data-variant-id'), delta, pid, st.target)
                        : changeUnits(card.getAttribute('data-variant-id'), delta);
    req.then(function (cart) {
      st.inflight = false;
      accept(cart, nextSeq());
      refreshDrawerSilently();
      var got = qtyIn(cart, pid);
      if (got !== st.sent) { settle(card, st); return; }   /* capped */
      if (st.target === got) st.target = null;
      st.sent = null;
      paint();
      flush(card);                                          /* taps made while in flight */
    }, function () {
      st.inflight = false;
      settle(card, st);                                     /* back to the last confirmed cart, once */
      refresh();
    });
  }
  function tap(card, dir) {
    var st = cardState.get(card);
    var base = st.target !== null ? st.target : confirmedQty(card);
    var next = Math.max(0, base + dir);
    if (next === base) return;
    st.target = next;
    clearError(card);
    paint();
    flush(card);
  }
  cards.forEach(function (card) {
    card.addEventListener('click', function (e) {
      var t = e.target.closest('[data-aov-add],[data-aov-inc],[data-aov-dec]');
      if (!t) return;
      e.preventDefault();
      e.stopPropagation();
      var hadFocus = document.activeElement === t;
      tap(card, t.hasAttribute('data-aov-dec') ? -1 : 1);
      /* Add hides itself under the tap, and so does the stepper at 0: keep focus
         on the control that replaces it. */
      if (hadFocus && t.closest('[hidden]')) {
        var to = card.querySelector(t.hasAttribute('data-aov-add') ? '[data-aov-inc]' : '[data-aov-add]');
        if (to) to.focus();
      }
    });
  });

  refresh();
})();
